import { useEffect, useState } from "react";
import { MessageSquare, Phone, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, fmtDateTime, telLink, waLink, pingNotifications } from "@/lib/api";
import { OutcomeSelect } from "@/components/OutcomeSelect";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/rbac";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const DEFAULT_TYPES = ["Call", "WhatsApp", "In-Person", "Site Visit", "Email", "Other"];

export const JobInteractionLog = ({ jobId, phone }) => {
  const { user } = useAuth();
  const canWrite = can(user, "interactions:write");
  const canRead = can(user, "interactions:read");
  const [logs, setLogs] = useState([]);
  const [types, setTypes] = useState(DEFAULT_TYPES);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ type: "", outcome: "", next_action: "" });
  const [saving, setSaving] = useState(false);

  const load = () =>
    api.get(`/jobs/${jobId}/interactions`).then(({ data }) => setLogs(data)).catch(() => {});

  useEffect(() => {
    if (!canRead) return;
    load();
    api
      .get("/meta")
      .then(({ data }) => data.job_interaction_types?.length && setTypes(data.job_interaction_types))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const save = async () => {
    if (!form.type) return toast.error("Choose an interaction type");
    setSaving(true);
    try {
      await api.post(`/jobs/${jobId}/interactions`, form);
      setForm({ type: "", outcome: "", next_action: "" });
      setAdding(false);
      toast.success("Interaction logged");
      pingNotifications();
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!canRead) return null;

  const contact = async (channel, url) => {
    if (phone && url) {
      if (channel === "Call") window.location.href = url;
      else window.open(url, "_blank");
    }
    if (!canWrite) return;
    try {
      await api.post(`/jobs/${jobId}/interactions`, { type: channel, outcome: "", next_action: "" });
      toast.success(`${channel} logged — add the response below`);
      pingNotifications();
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  return (
    <Card className="border-brand-line shadow-none" data-testid="job-interaction-log">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2 text-lg">
          <MessageSquare className="h-4 w-4 text-brand" strokeWidth={1.8} /> Interaction log
        </CardTitle>
        <div className="flex flex-wrap items-center gap-2">
          {phone && (
            <>
              <Button
                size="sm"
                variant="outline"
                className="min-h-[40px]"
                data-testid="job-call-btn"
                onClick={() => contact("Call", telLink(phone))}
              >
                <Phone className="mr-1 h-4 w-4" /> Call
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="min-h-[40px] border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                data-testid="job-whatsapp-btn"
                onClick={() => contact("WhatsApp", waLink(phone))}
              >
                <MessageSquare className="mr-1 h-4 w-4" /> WhatsApp
              </Button>
            </>
          )}
          {canWrite && !adding && (
            <Button
              size="sm"
              variant="outline"
              data-testid="add-job-interaction-btn"
              onClick={() => setAdding(true)}
            >
              <Plus className="mr-1 h-4 w-4" /> Log interaction
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {adding && (
          <div
            className="space-y-3 rounded-lg border border-brand-line bg-brand-mist/40 p-4"
            data-testid="job-interaction-form"
          >
            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <Label className="text-xs">Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="interaction-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {types.map((t) => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs">Outcome</Label>
                <Input
                  className="mt-1.5"
                  data-testid="interaction-outcome-input"
                  placeholder="What happened / customer response"
                  value={form.outcome}
                  onChange={(e) => setForm({ ...form, outcome: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Next action</Label>
              <Input
                className="mt-1.5"
                data-testid="interaction-next-action-input"
                placeholder="What needs to happen next"
                value={form.next_action}
                onChange={(e) => setForm({ ...form, next_action: e.target.value })}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setAdding(false);
                  setForm({ type: "", outcome: "", next_action: "" });
                }}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                data-testid="save-job-interaction-btn"
                disabled={saving}
                onClick={save}
              >
                Save entry
              </Button>
            </div>
            <p className="text-xs text-slate-400">
              Entries are stamped with your name and time and cannot be edited once saved.
            </p>
          </div>
        )}

        {logs.length === 0 ? (
          <p className="py-6 text-center text-sm text-slate-400" data-testid="job-interaction-empty">
            No interactions logged yet.
          </p>
        ) : (
          <ol className="space-y-3">
            {logs.map((l) => (
              <li
                key={l.id}
                className="rounded-lg border border-brand-line p-3"
                data-testid={`job-interaction-${l.id}`}
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="rounded-full bg-brand/10 px-2.5 py-0.5 text-xs font-semibold text-brand">
                    {l.type}
                  </span>
                  <span className="text-xs text-slate-400">
                    {l.staff_name} · {fmtDateTime(l.timestamp)}
                  </span>
                </div>
                {l.outcome ? (
                  <p className="mt-2 text-sm text-brand-ink">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                      Outcome:{" "}
                    </span>
                    {l.outcome}
                  </p>
                ) : (
                  canWrite && (
                    <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 p-2" data-testid={`pending-response-${l.id}`}>
                      <p className="mb-1 text-xs font-medium text-amber-700">Response pending — add the outcome:</p>
                      <OutcomeSelect
                        patchUrl={`/jobs/${jobId}/interactions/${l.id}`}
                        testid={`job-outcome-${l.id}`}
                        onSaved={load}
                      />
                    </div>
                  )
                )}
                {l.next_action && (
                  <p className="mt-1 text-sm text-brand-ink">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                      Next:{" "}
                    </span>
                    {l.next_action}
                  </p>
                )}
              </li>
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  );
};
